import React from "react";
import BaseModal from "@/components/reusables/Modals/BaseModal";
import { toastError } from "@helpers/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useShopifyOAuthInstallMutation } from "@redux/features/integrations/shopifyApi";
import BaseInput from "@reusables/BaseInput";
import UIButton from "@reusables/UIButton";
import { ExternalLink } from "lucide-react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formScheme = z.object({
    shop_url: z.string().trim(),
    client_id: z.string().trim(),
    client_secret: z.string().trim(),
});

type FormTyping = z.infer<typeof formScheme>;

type ShopifyIntegrationModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export default function ShopifyIntegrationModal({ isOpen, onClose }: ShopifyIntegrationModalProps) {
    const { t } = useTranslation("", {
        keyPrefix: "settings.integrations.thirdParty.integrations.shopify.integrationModal",
    });

    const form = useForm<FormTyping>({
        resolver: zodResolver(formScheme),
    });

    const [installShopify, { isLoading }] = useShopifyOAuthInstallMutation();

    const onSubmit = form.handleSubmit((data) => {
        const installRequest = {
            shop: data.shop_url,
            client_id: data.client_id,
            client_secret: data.client_secret,
        };

        installShopify(installRequest)
            .unwrap()
            .then((response) => {
                // Close modal before redirecting
                onClose();
                // Redirect to Shopify OAuth page
                window.location.href = response.auth_url;
            })
            .catch((e) => {
                toastError(e);
            });
    }, console.error);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={800}
            padding="45px"
            useCloseIcon
            isLoading={isLoading}
        >
            <div className="text-xl font-semibold text-center text-accent mb-4">{t("heading")}</div>
            <FormProvider {...form}>
                <form onSubmit={onSubmit} className="space-y-4">
                    {/* # Shop # */}
                    <Controller
                        control={form.control}
                        name={"shop_url"}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("connection.shop_url.label")}
                                placeholder={t("connection.shop_url.placeholder")}
                                required
                            />
                        )}
                    />

                    {/* # Client ID # */}
                    <Controller
                        control={form.control}
                        name={"client_id"}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("connection.client_id.label")}
                                placeholder={t("connection.client_id.placeholder")}
                                required
                            />
                        )}
                    />

                    {/* # Client Secret # */}
                    <Controller
                        control={form.control}
                        name={"client_secret"}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("connection.client_secret.label")}
                                placeholder={t("connection.client_secret.placeholder")}
                                required
                            />
                        )}
                    />

                    <div className={"mt-8 text-center"}>
                        <UIButton size="lg" type="submit" className="w-full">
                            <span>{t("buttons.connect")}</span>
                            <ExternalLink size={16} />
                        </UIButton>
                    </div>
                </form>
            </FormProvider>
        </BaseModal>
    );
}
